<template>
  <div class="page page--address" :class="!items ? 'loading' : ''">
    <Head>
      <title>{{ pageTitle }}</title>
      <body class="site--address" />
      <meta name="description" :content="description" />
      <meta name="og:title" :content="titleFormatted" />
      <meta name="og:description" :content="description" />
    </Head>
    <AdUnit unit="1" />
    <div class="section section--atlas">
      <h1 class="section__title">
        <router-link :to="{ name: 'countyAtlas' }"
          >{{ countyType[0] }} Map</router-link
        >
      </h1>
      <div class="section address-reading">
        <div v-if="titleFormatted" class="current-address">
          {{ titleFormatted }}
        </div>
      </div>
      <h2 v-if="selectedCounty">{{ countyWithTypeFormatted }} Map</h2>
      <AddressMap
        v-if="selectedCounty"
        :showMarker="false"
        :showNameOnHover="true"
        feature="county"
        :selectedFeature="selectedCounty"
        @featureClick="featureClick"
      />

      <CountyDetails
        v-if="selectedCounty"
        :fipsCode="selectedCounty.id"
        :countyType="countyType"
      />

      <div v-if="selectedCounty && !selectedState.ccds">
        <h2>{{ countyWithTypeFormatted }} Township Map</h2>
        <AddressMap
          :bbox="selectedCounty.bbox"
          :showMarker="false"
          :showNameOnHover="true"
          feature="township"
          :selectedCounty="selectedCounty.id"
          @featureClick="featureTownshipClick"
        />
        <p>
          This map shows sub-county divisions for {{ countyWithTypeFormatted }}.
          This can include Cities, Townships, Towns, Incorporated Places,
          Election Precints, Districts, Territories and other county
          subdivisions sourced from the United States Census.
          <router-link :to="{ name: 'township' }"
            >Find what township you are in now</router-link
          >.
        </p>
      </div>

      <div v-if="!items" class="spinner">
        <div class="double-bounce1"></div>
        <div class="double-bounce2"></div>
      </div>
      <div class="country">
        <h2 class="country__name" v-if="stateCounties">
          {{ stateFormatted }} {{ countyType[1] }}
        </h2>
        <h2 class="country__name" v-else>US Counties by State</h2>
        <div class="cities no-states">
          <div v-for="item in items" :key="item.id" class="city">
            <router-link :to="itemToRoute(item)">{{ item.name }}</router-link>
          </div>
        </div>
      </div>
      <h2>{{ stateFormatted }} {{ countyType[0] }} Map</h2>
      <AddressMap
        :bbox="[-127, 22.6, -64.9, 50.2]"
        :showMarker="false"
        :showNameOnHover="true"
        :disableSelection="!stateFormatted"
        feature="county"
        :selectedState="stateFormatted"
        @featureClick="featureClick"
      />
      <h3>{{ stateFormatted }}</h3>
      <Wikipedia v-if="stateFormatted" :articleTitle="stateWikipediaArticle" />
    </div>
    <TheFooter>
      <p>{{ description }}</p>
      <p>
        Find any county in the United Stated, along with resources and links to
        official county websites. See a county map and local details. Looking
        for what county you are currently in? Find out on this map or
        <router-link :to="{ name: 'county' }"
          >use your device GPS to see what county you are in</router-link
        >.
      </p>
      <template v-slot:nav>
        <template v-if="selectedCounty">
          <li>
            <router-link :to="countyElevationRoute"
              >{{ titleFormatted }} Elevation</router-link
            >
          </li>
          <li>
            <router-link :to="{ ...countyAddressRoute, name: 'snowTotals' }"
              >{{ titleFormatted }} Snow Accumulation</router-link
            >
          </li>
          <li>
            <router-link :to="{ ...countyAddressRoute, name: 'zone' }"
              >{{ titleFormatted }} USDA Plant Hardiness Zone</router-link
            >
          </li>
          <li>
            <router-link :to="{ ...countyAddressRoute, name: 'schoolDistrict' }"
              >{{ titleFormatted }} School Districts</router-link
            >
          </li>
          <li>
            <router-link :to="{ ...countyAddressRoute, name: 'nativeLands' }"
              >{{ titleFormatted }} Indigenous Territories</router-link
            >
          </li>
        </template>
        <template v-else>
          <li>
            <router-link :to="{ name: 'countyAtlas' }"
              >US County Map</router-link
            >
          </li>
          <li>
            <router-link :to="{ name: 'address' }"
              >What is my address?</router-link
            >
          </li>
          <li>
            <router-link :to="{ name: 'county' }"
              >What {{ countyType[0].toLowerCase() }} am I in?</router-link
            >
          </li>
          <li>
            <router-link :to="{ name: 'township' }"
              >What township am I in?</router-link
            >
          </li>
        </template>
      </template>
    </TheFooter>
  </div>
</template>

<script>
import TheFooter from "@/components/TheFooter.vue";
import CountyDetails from "@/components/CountyDetails.vue";
import Wikipedia from "@/components/Wikipedia.vue";
import states from "@/data/states";
import counties from "@/data/counties";
import { defineAsyncComponent } from "vue";
import { Head } from "@vueuse/head";
import { encodeUrlTitle, round } from "@/helpers";
import AdUnit from "@/components/AdUnit.vue";

export default {
  components: {
    AdUnit,
    AddressMap: defineAsyncComponent(() =>
      import("@/components/AddressMap.vue")
    ),
    TheFooter,
    Wikipedia,
    CountyDetails,
    Head
  },
  props: {
    state: {
      type: String
    },
    county: {
      type: String
    }
  },
  data() {
    return {
      states: Object.values(states).sort((a, b) =>
        a.name.localeCompare(b.name)
      ),
      counties,
      overrideAppRendered: true
    };
  },
  computed: {
    stateFormatted() {
      if (!this.state) {
        return null;
      }
      return this.format(this.state);
    },
    countyFormatted() {
      if (!this.county) {
        return null;
      }
      return this.format(this.county);
    },
    countyWithTypeFormatted() {
      if (!this.countyFormatted) {
        return null;
      }
      return `${this.countyFormatted} ${this.countyType[0]}`;
    },
    countyType() {
      let suffix = ["County", "Counties"];
      if (this.state === "alaska") {
        suffix = ["Borough", "Boroughs"];
      } else if (this.state === "louisiana") {
        suffix = ["Parish", "Parishes"];
      }
      return suffix;
    },
    selectedState() {
      if (!this.state) {
        return null;
      }
      return this.states.find(s => s.name === this.stateFormatted);
    },
    selectedCounty() {
      if (!this.stateCounties || !this.countyFormatted) {
        return null;
      }
      return this.stateCounties.find(
        c => c.name.toLowerCase() === this.countyFormatted.toLowerCase()
      );
    },
    stateCounties() {
      if (!this.selectedState || !this.counties) {
        return null;
      }
      const keys = Object.keys(this.counties).filter(
        key => key.substr(0, 2) === this.selectedState.id
      );
      const stateCounties = [];
      keys.forEach(key => {
        stateCounties.push({ ...this.counties[key], id: key });
      });
      return stateCounties;
    },
    items() {
      if (this.stateCounties) {
        return this.stateCounties;
      }
      return this.states;
    },
    titleFormatted() {
      if (this.countyFormatted) {
        return `${this.countyWithTypeFormatted}, ${this.stateFormatted}`;
      }
      if (this.stateFormatted) {
        return this.stateFormatted;
      }
      return "United States";
    },
    pageTitle() {
      if (!this.countyFormatted && !this.stateFormatted) {
        return `United State County Map - ${this.$t("address-title")}`;
      }
      let title = this.countyFormatted
        ? this.titleFormatted
        : `${this.titleFormatted} ${this.countyType[0]}`;
      return `${title} Map - What ${this.countyType[0]} Am I In? - ${this.$t(
        "address-title"
      )}`;
    },
    description() {
      if (this.countyFormatted) {
        return `View a map of ${
          this.titleFormatted
        }. Find what ${this.countyType[0].toLowerCase()} you are in. See nearby ${this.countyType[1].toLowerCase()} to ${
          this.countyWithTypeFormatted
        } and see official ${
          this.countyWithTypeFormatted
        } resources and information.`;
      }
      if (this.stateFormatted) {
        const countyType = this.countyType[0].toLowerCase();
        return `View a map of ${this.stateFormatted} by ${countyType}. Find what ${countyType} you are in, and see official ${this.stateFormatted} ${countyType} resources and information.`;
      }
      return "View a map of the United States by county. Find what county you are in, and see official United States county resources and information.";
    },
    stateWikipediaArticle() {
      if (!this.stateFormatted) {
        return null;
      }
      let article = this.stateFormatted.replace(/\s/g, "_");
      if (["New_York", "Washington", "Georgia"].includes(article)) {
        article += "_(state)";
      }
      return article;
    },
    countyElevationRoute() {
      if (!this.selectedCounty) {
        return null;
      }
      const bbox = this.selectedCounty.bbox;
      const longitude = round((bbox[0] + bbox[2]) / 2, 5);
      const latitude = round((bbox[1] + bbox[3]) / 2, 5);
      return {
        name: "location",
        params: {
          latitude,
          longitude,
          title: encodeUrlTitle(this.titleFormatted),
          wikipedia: encodeURI(this.titleFormatted),
          lang: this.$i18n.locale
        }
      };
    },
    countyAddressRoute() {
      if (!this.selectedCounty) {
        return null;
      }
      const bbox = this.selectedCounty.bbox;
      const lng = round((bbox[0] + bbox[2]) / 2, 5);
      const lat = round((bbox[1] + bbox[3]) / 2, 5);
      return {
        query: {
          lat,
          lng,
          title: encodeURI(this.titleFormatted)
        },
        params: {
          lang: this.$i18n.locale === "en" ? null : this.$i18n.locale
        }
      };
    }
  },
  methods: {
    itemToRoute(item) {
      return {
        name: "countyAtlas",
        params: {
          state: this.stateFormatted
            ? this.state
            : item.name.toLowerCase().replace(/\s/g, "-"),
          county: this.stateFormatted
            ? item.name.toLowerCase().replace(/\s/g, "-")
            : null
        }
      };
    },
    featureClick(e) {
      this.$router.push({
        name: "countyAtlas",
        params: {
          state: e.FULLNAME.split(", ")[1]
            .toLowerCase()
            .replace(/\s/g, "-"),
          county: e.BASENAME.toLowerCase().replace(/\s/g, "-")
        }
      });
    },
    featureTownshipClick(e) {
      console.log(e);
      // this.$router.push({
      //   name: 'countyAtlas',
      //   params: {
      //     state: e.FULLNAME.split(', ')[1].toLowerCase().replaceAll(' ', '-'),
      //     county: e.BASENAME.toLowerCase().replaceAll(' ', '-'),
      //   },
      // });
    },
    format(text) {
      return text
        .replace(/-/g, " ")
        .replace(/\w\S*/g, w => w.replace(/^\w/, c => c.toUpperCase()));
    }
  }
};
</script>
